import React, { useState, useEffect } from 'react';

import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { displayMessage } from '../../Utils/helper';
import { getWorkkflowCombinations } from '../../services/ApiManageWorkflow';
import { getOrderDetails } from '../../services/ApiIndentation';
import TableComponent from './TableComponent';
import GraphComponent from './GraphComponent';
import styled from "styled-components";
import PageHeader from '../common/PageHeader';

function IndentationPage() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [combinations, setCombinations] = useState([]);
    const [safetystock, setSafetystock] = useState(null);
    const [eoq, setEOQ] = useState(null);
    const [ReportData, setReportData] = useState(null);

    useEffect(() => {
        const init = async () => {
            const data = await getWorkkflowCombinations(state?.workflow?.workflow_id);
            setCombinations(data || []);
        };
        init();
    }, [state?.workflow?.workflow_id]);

    function getFormattedPath(obj) {
        const levels = ["level1", "level2", "level3", "level4", "level5"];
        const path = [];
        for (let key of levels) {
            if (!obj[key]) break;
            path.push(obj[key]);
        }
        return path.join("/");
    }

    const options = combinations.map(ob => ({
        label: `${ob.sku_name} - ${getFormattedPath(ob)}`,
        value: {
            sku_name: ob.sku_name,
            location: getFormattedPath(ob),
            sku_id: ob.sku_id
        }
    }));

    const combinationSelectChangeHandler = async (selected) => {
        try {
            const result = await getOrderDetails(selected?.value?.sku_name, state?.workflow?.workflow_id);
            if (result !== undefined) {
                setSafetystock(result[selected?.value?.sku_name][0]['Safety_Stock']);
                setEOQ(result[selected?.value?.sku_name][0]['EOQ']);
                setReportData(result.Values);
            }
        } catch (err) {
            console.error(err);
            setEOQ(null);
            setSafetystock(null);
            setReportData(null);
            displayMessage("danger", "Error", "Error occurred while fetching details");
        }
    };

    const goToScm = () => {
        const workflow = state?.workflow;
        if (!workflow?.workflow_id) {
            displayMessage("warning", "Workflow missing", "Open this report from a workflow first.");
            return;
        }
        navigate("/SupplyChainManagement/detail", {
            state: { workflow, initialStep: "inventory" },
        });
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    return (
        <>
            <Wrapper>
                <PageHeader
                    title="Inventory Optimization Report"
                    subtitle={
                        state?.workflow?.workflow_name
                            ? `${state.workflow.workflow_name} — reconciling forecast with inventory planning.`
                            : "Reconciling forecast with inventory planning."
                    }
                />

                <ControlPanel>
                    <TopRow>
                            <div className="left">
                                <label>Combination</label>
                                <StyledSelect
                                    classNamePrefix="react-select"
                                    closeMenuOnSelect={true}
                                    onChange={combinationSelectChangeHandler}
                                    options={options}
                                    placeholder="Select SKU / Location"
                                    isSearchable
                                />
                            </div>
                            <div className="right">
                                <button type="button" className="metric-btn">EOQ : {eoq ?? "--"}</button>
                                <button type="button" className="metric-btn">Safety Stock : {safetystock ?? "--"}</button>
                                <button type="button" className="export-btn">Export</button>
                            </div>
                        </TopRow>
                </ControlPanel>

                {!ReportData ? (
                    <EmptyState>
                        <p>Please select a combination to view the report</p>
                    </EmptyState>
                ) : (
                    <>
                        <ReportsGrid>
                            <GridPanel>
                                <TableComponent data={ReportData} />
                            </GridPanel>
                            <GridPanel>
                                <GraphComponent data={ReportData} />
                            </GridPanel>
                        </ReportsGrid>
                        <ScmActions>
                            <button type="button" className="scm-btn" onClick={goToScm}>
                                Go to SCM
                            </button>
                        </ScmActions>
                    </>
                )}
            </Wrapper>
        </>
    );
}

const EmptyState = styled.div`
    flex: 1;
    background: #fff;
    border: 1px dashed #e6eef4;
    border-radius: 12px;
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);

    p {
        margin: 0;
        padding: 24px;
        text-align: center;
        color: #6a7d8c;
        font-size: 14px;
        font-family: Poppins, sans-serif;
    }
`;

const ScmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 0 2px;

  .scm-btn {
    background: #E06639;
    color: #ffffff;
    font-weight: 600;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    transition: 0.2s ease;
    box-shadow: 0 2px 8px rgba(224, 102, 57, 0.2);
    font-size: 13px;
    font-family: Poppins, sans-serif;
    white-space: nowrap;
  }

  .scm-btn:hover {
    transform: translateY(-1px);
    background: #c9552e;
  }
`;

export default IndentationPage;

const Wrapper = styled.div`
  width: 100%;
  min-height: calc(100dvh - 118px);
  padding: 2px 2px 6px;
  background: transparent;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReportsGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: 1fr;
  gap: 10px;
  min-height: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const GridPanel = styled.div`
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1100px) {
    min-height: 42vh;
  }
`;

const ControlPanel = styled.section`
  background: #fff;
  border: 1px solid #e6eef4;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
  border-left: 4px solid #032B4E;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  gap: 10px 12px;
  align-items: end;
  width: 100%;

  .left {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  label {
    font-weight: 600;
    color: #0c3c54;
    margin-bottom: 4px;
    font-size: 12px;
    font-family: Poppins, sans-serif;
  }

  .right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .metric-btn {
    background: linear-gradient(135deg, #021a30 0%, #032B4E 55%, #032B4E 100%);
    color: #fff;
    font-weight: 600;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(3, 43, 78, 0.12);
    cursor: default;
    font-size: 12px;
    font-family: Poppins, sans-serif;
    white-space: nowrap;
  }

  .export-btn {
    background: #E06639;
    color: #ffffff;
    font-weight: 600;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: 0.2s ease;
    box-shadow: 0 2px 8px rgba(224, 102, 57, 0.2);
    font-size: 12px;
    font-family: Poppins, sans-serif;
    white-space: nowrap;
  }

  .export-btn:hover {
    transform: translateY(-1px);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    .right {
      justify-content: flex-start;
    }
  }
`;

const StyledSelect = styled(Select)`
  .react-select__control {
    border-radius: 6px !important;
    border: 1px solid #d9e4ee !important;
    background: #ffffff !important;
    box-shadow: 0 1px 2px rgba(12, 60, 84, 0.05) !important;
    transition: all 0.2s ease !important;
    font-weight: 500;
    font-size: 12px;
    font-family: Poppins, sans-serif;
    min-height: 34px;
  }

  .react-select__control--is-focused {
    border-color: #032B4E !important;
    box-shadow: 0 0 0 2px rgba(3, 43, 78, 0.12) !important;
  }

  .react-select__control:hover {
    border-color: #d5dee8 !important;
  }

  .react-select__value-container {
    padding: 0 10px !important;
  }

  .react-select__placeholder {
    color: #7b8b99 !important;
    font-family: Poppins, sans-serif;
  }

  .react-select__single-value {
    color: #0c3c54 !important;
  }

  .react-select__indicator-separator {
    background: #d5dee8 !important;
  }

  .react-select__dropdown-indicator {
    color: #6a7d8c !important;
  }

  .react-select__dropdown-indicator:hover {
    color: #032B4E !important;
  }

  .react-select__menu {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e6eef4;
    box-shadow: 0 6px 20px rgba(12, 60, 84, 0.12);
  }

  .react-select__option {
    font-weight: 400;
    font-size: 14px;
    font-family: Poppins, sans-serif;
  }

  .react-select__option--is-focused {
    background: #f4f3ef !important;
    color: #0c3c54 !important;
  }

  .react-select__option--is-selected {
    background: #E06639 !important;
    color: #fff !important;
  }
`;
